import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, FileText, Layers, Lock, Mail, Phone, Send, ShieldCheck, UserRound, UsersRound } from "lucide-react";
import client from "@/api/client";
import { cn } from "@/lib/utils";
import { ROLE_LABELS } from "@/lib/format";
import { ROOM_STOCK_IMAGES } from "@/lib/stockImages";
import { Field, IconInput, inputShell, selectShell } from "@/components/forms/FormField";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Assignable roles per user type (mirrors CreateUserDto on the backend).
const ROLES_BY_TYPE = {
  internal: ["ACCOUNT_MANAGER", "SALES_EXECUTIVE"],
  external: ["EXTERNAL_ARCHITECT", "EXTERNAL_DESIGNER"],
};

const USER_TYPES = [
  { value: "internal", label: "Internal User" },
  { value: "external", label: "External Partner" },
];

const FEATURES = [
  { icon: UsersRound, tone: "bg-accent/10 text-accent", title: "Assign roles", body: "Control access with predefined roles and permissions." },
  { icon: ShieldCheck, tone: "bg-success/10 text-success", title: "Keep your data secure", body: "Give access only to what they need." },
  { icon: Send, tone: "bg-chart-3/10 text-chart-3", title: "Get started quickly", body: "They can sign in right away with the email and password you set." },
];

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", userType: "internal", roleName: "", active: true, notes: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Enter the member's name";
  if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address";
  if (form.password.length < 8) errors.password = "Use at least 8 characters";
  if (!form.roleName) errors.roleName = "Choose a role";
  return errors;
}

// Wooden "people" tokens over a warm room photo, fading into the cream panel.
function PromoPanel() {
  return (
    <aside className="hidden md:flex flex-col bg-sidebar/70 border-r border-border">
      <div className="relative h-60 shrink-0 overflow-hidden">
        <img src={ROOM_STOCK_IMAGES.living} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--sidebar)/0.25)] to-[hsl(var(--sidebar))]" />
        <div className="absolute inset-x-0 bottom-8 flex items-end justify-center gap-3" aria-hidden="true">
          {[{ s: "h-16 w-16", i: "h-7 w-7" }, { s: "h-24 w-24", i: "h-11 w-11" }, { s: "h-16 w-16", i: "h-7 w-7" }].map((t, idx) => (
            <span
              key={idx}
              className={cn(
                "rounded-full flex items-center justify-center bg-gradient-to-br from-[#EBD3AE] to-[#C9A273] shadow-[0_8px_18px_rgba(60,40,20,0.35),inset_0_2px_3px_rgba(255,255,255,0.5)]",
                t.s,
              )}
            >
              <UserRound className={cn("text-[#5B3F28]", t.i)} fill="currentColor" strokeWidth={1.25} />
            </span>
          ))}
        </div>
      </div>

      <div className="px-7 pb-7 pt-2 flex-1 flex flex-col">
        <h2 className="font-serif text-[1.9rem] leading-tight text-foreground">Add a Team Member</h2>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          Invite your team to collaborate on projects, manage enquiries and organise your catalogue.
        </p>
        <ul className="mt-6 space-y-4">
          {FEATURES.map(({ icon: Icon, tone, title, body }) => (
            <li key={title} className="flex items-start gap-3.5">
              <span className={cn("h-11 w-11 rounded-full flex items-center justify-center shrink-0", tone)}>
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export default function AddMemberDialog({ open, setOpen }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const onInput = (key) => (e) => set(key)(e.target.value);

  const reset = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setShowPassword(false);
  };

  const createMutation = useMutation({
    mutationFn: (payload) => client.post("/users", payload),
    onSuccess: () => {
      toast.success("Team member added");
      setOpen(false);
      reset();
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to create user"),
  });

  const changeType = (userType) =>
    setForm((f) => ({ ...f, userType, roleName: ROLES_BY_TYPE[userType].includes(f.roleName) ? f.roleName : "" }));

  const submit = (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    createMutation.mutate({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      password: form.password,
      roleName: form.roleName,
      status: form.active ? "ACTIVE" : "DISABLED",
      notes: form.notes.trim() || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogContent
        data-testid="add-team-member-dialog"
        className="max-w-[calc(100vw-2rem)] md:max-w-4xl p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl border-border md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
      >
        <PromoPanel />

        <form onSubmit={submit} noValidate className="max-h-[90vh] overflow-y-auto px-6 sm:px-9 py-8">
          <DialogTitle className="font-serif text-2xl sm:text-[1.75rem] font-normal text-foreground pr-6">Add Team Member</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground">
            Create a new user and assign a role to give them access to the platform.
          </DialogDescription>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <Field label="Full Name" required error={errors.name} htmlFor="team-name" className="sm:col-span-2">
              <IconInput id="team-name" icon={UserRound} data-testid="team-name-input" placeholder="e.g. Priya Kapoor" autoComplete="off" value={form.name} onChange={onInput("name")} error={errors.name} />
            </Field>

            <Field label="Email Address" required error={errors.email} htmlFor="team-email">
              <IconInput id="team-email" icon={Mail} type="email" data-testid="team-email-input" placeholder="e.g. priya@homeincludes.com" autoComplete="off" value={form.email} onChange={onInput("email")} error={errors.email} />
            </Field>
            <Field label="Phone Number" htmlFor="team-phone">
              <IconInput id="team-phone" icon={Phone} type="tel" data-testid="team-phone-input" placeholder="e.g. +91 98765 43210" value={form.phone} onChange={onInput("phone")} />
            </Field>

            <Field label="Role" required error={errors.roleName}>
              <Select value={form.roleName} onValueChange={set("roleName")}>
                <SelectTrigger
                  data-testid="team-role-select"
                  aria-invalid={Boolean(errors.roleName)}
                  className={selectShell(errors.roleName)}
                >
                  <span className="flex items-center gap-2.5 truncate">
                    <Layers className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                    <SelectValue placeholder="Select a role" />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {ROLES_BY_TYPE[form.userType].map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="User Type" required>
              <div role="radiogroup" aria-label="User type" className="grid grid-cols-2 gap-2">
                {USER_TYPES.map((t) => {
                  const checked = form.userType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      data-testid={`team-type-${t.value}`}
                      onClick={() => changeType(t.value)}
                      className={cn(
                        "flex items-center gap-2 rounded-lg border px-3 h-11 text-left text-xs leading-tight transition-colors",
                        checked ? "border-accent/60 bg-accent/5 text-foreground" : "border-border bg-card text-foreground/80 hover:border-foreground/30",
                      )}
                    >
                      <span className={cn("h-4 w-4 rounded-full border flex items-center justify-center shrink-0", checked ? "border-accent" : "border-muted-foreground/50")}>
                        {checked && <span className="h-2 w-2 rounded-full bg-accent" />}
                      </span>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Password" required error={errors.password} htmlFor="team-password" className="sm:col-span-2">
              <IconInput
                id="team-password"
                icon={Lock}
                type={showPassword ? "text" : "password"}
                data-testid="team-password-input"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                value={form.password}
                onChange={onInput("password")}
                error={errors.password}
                trailing={
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
              />
              {!errors.password && <p className="mt-1 text-xs text-muted-foreground">Share it with the member securely; they'll use it to sign in.</p>}
            </Field>

            <div className="sm:col-span-2">
              <p className="text-sm font-medium text-foreground mb-2">Status</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch data-testid="team-status-switch" checked={form.active} onCheckedChange={set("active")} className="mt-0.5" />
                <span>
                  <span className="block text-sm text-foreground">{form.active ? "Active" : "Inactive"}</span>
                  <span className="block text-xs text-muted-foreground">
                    {form.active
                      ? "The user will be able to log in and access the platform."
                      : "The account is created but can't sign in until you activate it."}
                  </span>
                </span>
              </label>
            </div>

            <Field label="Additional Notes" optional htmlFor="team-notes" className="sm:col-span-2 pt-5 border-t border-border">
              <div className={cn(inputShell, "h-auto items-start py-3 border-border")}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.75} />
                <textarea
                  id="team-notes"
                  data-testid="team-notes-input"
                  rows={3}
                  maxLength={1000}
                  placeholder="Add any notes, responsibilities or custom information…"
                  value={form.notes}
                  onChange={onInput("notes")}
                  className="flex-1 min-w-0 resize-y bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
            </Field>
          </div>

          <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button type="button" variant="outline" className="sm:min-w-[8.5rem] border-accent/40 text-accent hover:bg-accent/5 hover:text-accent" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="team-submit-button"
              disabled={createMutation.isPending}
              className="sm:min-w-[8.5rem] bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {createMutation.isPending ? "Creating…" : "Create Member"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
